import { SortByQueryOptions } from '@bpm-web-app/download-api-sdk';
import { useFeed, useFollowedArtists, useGetMeSubscription } from '@bpm-web-app/swr-hooks';
import { useSupremeFilterParams, useUserSettings } from '@bpm-web-app/utils';
import { useContext, useMemo } from 'react';
import { LibraryTabsContext } from '../../../../utils/src/lib/library-tabs.context';
import { SecondaryPageTitle } from '../shared';
import { SeeMoreTracklistLink } from '../shared/see-more-button/see-more-btn';
import { TrackListSupreme } from '../shared/track-list/supreme/track-list-supreme';
import { TrackListPresetSupreme } from '../shared/track-list/supreme/track-list-supreme-helpers';
import styles from './your-feed-preview.module.css';

const PREVIEW_TRACK_COUNT = 10;

export function YourFeedPreview() {
    const { libraryProperty } = useContext(LibraryTabsContext);
    const { isAnonymous } = useUserSettings();
    const { data: subscriptionData } = useGetMeSubscription('supreme');
    const hasMembership = useMemo(() => subscriptionData?.data?.membership?.has_membership, [subscriptionData?.data?.membership?.has_membership]);

    /* Feed is a paid-only feature — same gate as the full Your Feed page. isAnonymous is known
     * synchronously, but membership resolves async, so the two are tracked separately below to
     * keep the homepage from reflowing once it lands. */
    const isFeedLocked = isAnonymous || !hasMembership;

    const { data: followedArtistsData, isLoading: isLoadingFollowedArtists } = useFollowedArtists(libraryProperty);

    const followedArtistSlugs = useMemo(
        () => (isFeedLocked ? [] : followedArtistsData?.data?.map((artist) => artist.slug) || []),
        [followedArtistsData, isFeedLocked]
    );

    /* Feed derives the follow set server-side — strip artist and request just the preview count. */
    const baseQuery = useSupremeFilterParams(true);
    const feedQuery = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { artist, ...filters } = baseQuery;
        return { ...filters, sort_by: SortByQueryOptions.DateDesc, limit: PREVIEW_TRACK_COUNT };
    }, [baseQuery]);
    const shouldFetchAlbums = !isFeedLocked && followedArtistSlugs.length > 0;

    const { data: feedData } = useFeed(feedQuery, shouldFetchAlbums);
    const previewAlbums = useMemo(() => feedData?.data || [], [feedData]);

    /* Anonymous is known on the first render, so those users never see this section at all.
     * Paid membership isn't, so hold the section's space with a skeleton while it resolves rather
     * than popping it in afterwards and pushing the sections below it down. */
    const isResolvingAccess = !isAnonymous && hasMembership === undefined;
    const isLoading = isResolvingAccess || isLoadingFollowedArtists || (shouldFetchAlbums && feedData === undefined);

    if (isAnonymous || hasMembership === false) return null;

    /* Nothing to preview for a member who doesn't follow anyone yet — hide the section rather than
     * showing an empty state on the homepage. Only decide this once we actually know. */
    if (!isLoading && followedArtistSlugs.length === 0) return null;

    return (
        <div className="spacing__window">
            <SecondaryPageTitle title="Your Feed" noPadding />
            <div className={styles['your-feed-preview__list']}>
                <TrackListSupreme
                    preset={TrackListPresetSupreme.Download}
                    list={previewAlbums}
                    isLoading={isLoading}
                    isSortable={false}
                    highlightRecentReleaseDate
                />
            </div>
            <SeeMoreTracklistLink href="/feed" expand={false} />
        </div>
    );
}

export default YourFeedPreview;
